import { create } from 'zustand';
import { cloneDeep, find, get, uniqBy } from 'lodash';

type IOptionItemProps = { value: string }[];
type ITagKeyOptionListProps = { [eventName: string]: IOptionItemProps };

interface ITrackMappedEventSensorStore {
  eventNameOptions: IOptionItemProps;
  setNameEventOptions: (eventNameOptions: IOptionItemProps) => void;

  tagKeyOptionList: ITagKeyOptionListProps;
  setTagKeyOptionList: (eventName: string, tagKeyOption: IOptionItemProps) => void;
}

export const useTrackMappedEventSensorStore = create<ITrackMappedEventSensorStore>((set) => ({
  eventNameOptions: [],
  setNameEventOptions: (eventNameOptions) => set(() => ({ eventNameOptions })),

  tagKeyOptionList: {},
  setTagKeyOptionList: (eventName, tagKeyOption) =>
    set((state) => {
      const updateTagKeyOption = uniqBy(
        [...(state.tagKeyOptionList?.[eventName] ?? []), ...tagKeyOption],
        'value',
      );
      return {
        tagKeyOptionList: {
          ...state.tagKeyOptionList,
          [eventName]: updateTagKeyOption,
        },
      };
    }),
}));

/**
 * Add the isManual field in rules for each rule object and tag_key in mapping_rules
 * @param mappedEvent Raw event data
 * @returns Event data after the isManual field is added
 */
export const addIsManualFieldToMappedEvent = (mappedEvent: any) => {
  // Prevent modification of the original object, deep copy a copy of the data
  const clonedMappedEvent = cloneDeep(mappedEvent);

  // Get eventNameOptions and tagKeyOptionList from the store
  const { eventNameOptions, tagKeyOptionList } = useTrackMappedEventSensorStore.getState();

  const manualFlags: boolean[] = [];

  // Traverse the two-dimensional array of rules, preserving the original structure for conversion
  const updatedRules = clonedMappedEvent.rules.map((ruleGroup: any[]) => {
    return ruleGroup.map((rule: any) => {
      // Determines whether the first value of the rule's value exists in eventNameOptions
      const ruleValue: string = get(rule, 'value[0]', '');
      const isManualEventName = !find(eventNameOptions, { value: ruleValue });
      rule.isManual = isManualEventName;
      manualFlags.push(isManualEventName);

      // For each rule, determine if mapping_rules (also a two-dimensional array) exist
      if (rule.mapping_rules && Array.isArray(rule.mapping_rules)) {
        rule.mapping_rules = rule.mapping_rules.map((mappingRuleGroup: any[]) => {
          return mappingRuleGroup.map((mappingRule: any) => {
            const tagKeyValue: string = get(mappingRule, 'tag_key.value[0]', '');
            // Gets the tagKeyOptions corresponding to the current event_name, or an empty array if none exists
            const tagKeyOptions = tagKeyOptionList[ruleValue] || [];
            const isManualTagKey = !find(tagKeyOptions, { value: tagKeyValue });
            mappingRule.tag_key.isManual = isManualTagKey;
            manualFlags.push(isManualTagKey);
            return mappingRule;
          });
        });
      }
      return rule;
    });
  });

  const isManual = manualFlags.some((flag) => flag);

  return { ...clonedMappedEvent, rules: updatedRules, isManual };
};
